import { DestroyRef, Injectable, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SwUpdate } from "@angular/service-worker";
import { I18nService } from "@app/core/services/i18n.service";
import { NotifyService } from "@app/core/services/notify.service";
import { forkJoin, take } from "rxjs";

/**
 * NOTE: In order to test the service worker locally
 *
 * 1. install @see https://www.npmjs.com/package/http-server-spa
 * 2. Build your project `ng build` then use the command `http-server-spa dist index.html 8080`
 * 3. Now you are running the built project and you should see an active service worker in the manifest.
 *
 * In order to test your changes:
 * 1. Make a change in the application, it could be as simple as adding a random <p></p> somewhere.
 * 2. Re-build the project after the change. If on a live server, you need to move the new build into the folder.
 * 3. After the project is built, you will see a notification pop up on the browser saying there is an update.
 * 4. If you want to test the home screen pop up, change the port number of the command above to one you have not used. It will prompt the event.
 */
@Injectable({
  providedIn: "root"
})
export class ServiceWorkerService {
  private swUpdate = inject(SwUpdate);
  private i18nService = inject(I18nService);
  private notifyService = inject(NotifyService);
  private _destroyRef = inject(DestroyRef);

  constructor() {
    this.checkForUnrecoverable();
    this.checkForAvailableUpdates();
  }

  /**
   * Check the activate service worker for any updates to the application.
   */
  checkForUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.checkForUpdate();
  }

  /**
   * In some cases, the version of the application used by the service worker to serve a client might be in a broken state that cannot be recovered from without a full page reload.
   * This subscription will monitor for this event.
   */
  checkForUnrecoverable() {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.unrecoverable.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      window.location.reload();
    });
  }

  /**
   * Monitor for an update to the application.
   * This will prompt the user to update if one is available.
   * @returns
   */
  checkForAvailableUpdates() {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((evt) => {
      switch (evt.type) {
        case "VERSION_DETECTED": {
          this._sendDownloadMessage();
          break;
        }
        case "VERSION_READY": {
          this._sendUpdateMessage();
          break;
        }
        case "VERSION_INSTALLATION_FAILED": {
          this._sendFailedMessage();
          break;
        }
      }
    });
  }

  /**
   * Send notification to user that a new version is downloading
   */
  private _sendDownloadMessage() {
    this.i18nService.translateAsync("APP.LBL_PWA_DOWNLOAD").subscribe((msg) => {
      this.notifyService.reload(null, msg);
    });
  }

  /**
   * Send notification to user that there is a new version.
   */
  private _sendUpdateMessage() {
    forkJoin({
      msg: this.i18nService.translateAsync("APP.LBL_PWA_UPDATE").pipe(take(1)),
      action: this.i18nService.translateAsync("APP.LBL_PWA_RELOAD").pipe(take(1))
    }).subscribe((results) => {
      const snack = this.notifyService.reload(null, results.msg, results.action);
      snack.onAction().subscribe(() => {
        this.swUpdate.activateUpdate().then(() => window.location.reload());
      });
    });
  }

  /**
   * Send notification to user that there is a new version and it failed to install.
   */
  private _sendFailedMessage() {
    forkJoin({
      msg: this.i18nService.translateAsync("APP.LBL_PWA_FAILED").pipe(take(1)),
      action: this.i18nService.translateAsync("APP.LBL_PWA_RELOAD").pipe(take(1))
    }).subscribe((results) => {
      const snack = this.notifyService.reload(null, results.msg, results.action);
      snack.onAction().subscribe(() => {
        this.swUpdate.activateUpdate().then(() => window.location.reload());
      });
    });
  }
}
